import { Cell, CrystalKind, EnemyKind } from "./types";

export interface Dims { cols: number; rows: number; }
export const MOBILE_DIMS: Dims = { cols: 11, rows: 11 };
export const DESKTOP_DIMS: Dims = { cols: 16, rows: 11 };

export const MAX_LEVEL = 50;
export const INTRO_LEVEL: Record<EnemyKind, number> = { scorpion: 1, spider: 2, snake: 3, bat: 12, bouncer: 14 };

export interface LevelData {
  grid: Cell[][];
  kinds: (CrystalKind | null)[][];
  player: { x: number; y: number };
  enemies: { x: number; y: number; kind: EnemyKind }[];
  reserve: EnemyKind[];
  cap: number;
}

let seed = 1;
const seedRandom = () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = seed;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const rnd = (n: number) => Math.floor(seedRandom() * n);
const N4 = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

export const inBounds = (x: number, y: number, cols: number, rows: number) => x >= 0 && y >= 0 && x < cols && y < rows;

function connected(grid: Cell[][], passable: (c: Cell) => boolean): boolean {
  const rows = grid.length, cols = grid[0].length;
  const cells: number[] = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++) if (passable(grid[y][x])) cells.push(y * cols + x);
  if (!cells.length) return false;
  const seen = new Set<number>([cells[0]]);
  const stack = [cells[0]];
  while (stack.length) {
    const k = stack.pop()!;
    const x = k % cols;
    const y = Math.floor(k / cols);
    for (const [dx, dy] of N4) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(nx, ny, cols, rows) || !passable(grid[ny][nx])) continue;
      const nk = ny * cols + nx;
      if (!seen.has(nk)) {
        seen.add(nk);
        stack.push(nk);
      }
    }
  }
  return seen.size === cells.length;
}

function floorNeighbors(grid: Cell[][], x: number, y: number) {
  const rows = grid.length, cols = grid[0].length;
  return N4.filter(([dx, dy]) => inBounds(x + dx, y + dy, cols, rows) && grid[y + dy][x + dx] === Cell.Floor).length;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rnd(i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export const capFor = (level: number, small = false) => (level <= 13 ? (level <= 3 ? 3 : 4) : Math.min(small ? 4 : 5, 4 + Math.floor((level - 14) / 11)));
export const initialCountFor = (level: number) => (level <= 1 ? 2 : capFor(level) - 1);
export const reinforcementsFor = (level: number) => 3 + Math.floor((level - 1) / 10) + (level === 5 ? 2 : 0);
export const totalEnemiesFor = (level: number) => initialCountFor(level) + reinforcementsFor(level);

function enemyKindsFor(level: number, total: number): EnemyKind[] {
  const weights: [EnemyKind, number][] = [
    ["scorpion", 3],
    ["spider", level >= INTRO_LEVEL.spider ? 2 : 0],
    ["snake", level >= INTRO_LEVEL.snake ? 1.5 : 0],
    ["bat", level >= INTRO_LEVEL.bat ? 1 : 0],
    ["bouncer", level >= INTRO_LEVEL.bouncer ? 1 : 0],
  ];
  const sum = weights.reduce((a, [, w]) => a + w, 0);
  const kinds: EnemyKind[] = [];
  for (let i = 0; i < total; i++) {
    let r = seedRandom() * sum;
    let pick: EnemyKind = "scorpion";
    for (const [k, w] of weights) { r -= w; if (r <= 0) { pick = k; break; } }
    kinds.push(pick);
  }
  const fresh = (Object.keys(INTRO_LEVEL) as EnemyKind[]).find((k) => INTRO_LEVEL[k] === level && k !== "scorpion");
  if (fresh && !kinds.includes(fresh)) kinds[kinds.length - 1] = fresh;
  const limit: Partial<Record<EnemyKind, number>> = { bat: 2, bouncer: 2 };
  let fast = 0;
  const seen: Partial<Record<EnemyKind, number>> = {};
  for (let i = 0; i < kinds.length; i++) {
    const k = kinds[i];
    const max = limit[k];
    if (max === undefined) continue;
    seen[k] = (seen[k] ?? 0) + 1;
    fast++;
    if (seen[k]! > max || fast > 3) { kinds[i] = seedRandom() < 0.5 ? "scorpion" : "spider"; seen[k]!--; fast--; }
  }
  return kinds;
}

export function generateLevel(level: number, dims: Dims = MOBILE_DIMS, seedOverride?: number): LevelData {
  const { cols, rows } = dims;
  const area = (cols * rows) / 121;
  seed = seedOverride ?? level * 7919 + cols * 131 + rows;
  const half = Math.ceil(cols / 2);
  for (let attempt = 0; attempt < 500; attempt++) {
    const grid: Cell[][] = Array.from({ length: rows }, () => Array(cols).fill(Cell.Floor));
    const ballCount = Math.round(Math.min(18 + level * 2, 34) * area);
    let placed = 0;
    for (let guard = 0; guard < 400 && placed < ballCount; guard++) {
      const x = rnd(half), y = rnd(rows);
      const mx = cols - 1 - x;
      if (grid[y][x] !== Cell.Floor) continue;
      grid[y][x] = Cell.Crystal;
      placed++;
      if (mx !== x && grid[y][mx] === Cell.Floor && placed < ballCount) { grid[y][mx] = Cell.Crystal; placed++; }
    }
    if (!connected(grid, (c) => c === Cell.Floor)) continue;

    const floors = shuffle(
      grid.flatMap((row, y) => row.map((c, x) => (c === Cell.Floor ? { x, y } : null)).filter(Boolean) as { x: number; y: number }[])
    );
    const player = floors.find((p) => p.y >= rows - 3 && Math.abs(p.x - (cols - 1) / 2) <= 1 && floorNeighbors(grid, p.x, p.y) >= 2)
      ?? floors.find((p) => floorNeighbors(grid, p.x, p.y) >= 2) ?? floors[0];
    const count = initialCountFor(level);
    const allKinds = enemyKindsFor(level, totalEnemiesFor(level) - (cols * rows <= 81 && level >= 20 ? 1 : 0));
    const candidates = floors.filter(
      (p) => Math.abs(p.x - player.x) + Math.abs(p.y - player.y) >= 5 && floorNeighbors(grid, p.x, p.y) >= 1
    );
    if (candidates.length < count) continue;

    const enemies = candidates.slice(0, count).map((p, i) => ({ ...p, kind: allKinds[i] }));
    const reserve = allKinds.slice(count);
    const kinds = grid.map((row) => row.map((c) => (c === Cell.Crystal ? "ball" : null))) as (CrystalKind | null)[][];
    const balls = shuffle(grid.flatMap((row, y) => row.map((c, x) => (c === Cell.Crystal ? { x, y } : null)).filter(Boolean) as { x: number; y: number }[]));
    const diamonds: { x: number; y: number }[] = [];
    for (const b of balls) {
      if (diamonds.length >= 3) break;
      if (diamonds.every((d) => Math.abs(d.x - b.x) + Math.abs(d.y - b.y) >= 3)) diamonds.push(b);
    }
    if (diamonds.length < 3) continue;
    diamonds.forEach((d) => (kinds[d.y][d.x] = "diamond"));
    const rest = balls.filter((b) => !diamonds.includes(b));
    const specials: CrystalKind[] = [];
    if (level >= 2) specials.push("bounce1", ...(level >= 6 ? ["bounce1" as CrystalKind] : []));
    if (level >= 3) specials.push("bounce2", ...(level >= 8 ? ["bounce2" as CrystalKind] : []));
    if (level >= 3) specials.push("ice");
    if (level >= 4) specials.push("bomb", ...(level >= 7 ? ["bomb" as CrystalKind] : []));
    specials.forEach((k, i) => { if (rest[i]) kinds[rest[i].y][rest[i].x] = k; });
    return { grid, kinds, player, enemies, reserve, cap: capFor(level, cols * rows <= 81) };
  }
  throw new Error("Level generation failed");
}

export const isBonusAfter = (level: number) => level % 5 === 0 && level < MAX_LEVEL;
export const TUNNEL_COLS = 17;
export const TUNNEL_ROWS = 30;

export interface TunnelData extends LevelData {
  exit: { x: number; y: number };
}

export function generateTunnel(afterLevel: number): TunnelData {
  const cols = TUNNEL_COLS, rows = TUNNEL_ROWS;
  seed = afterLevel * 104729 + 17;
  const grid: Cell[][] = Array.from({ length: rows }, () => Array(cols).fill(Cell.Wall));
  const path: { x: number; y: number }[] = [];
  const carve = (x: number, y: number) => {
    if (!inBounds(x, y, cols, rows)) return;
    if (grid[y][x] === Cell.Wall) path.push({ x, y });
    grid[y][x] = Cell.Floor;
  };
  const carve2 = (cx: number, cy: number) => { carve(cx, cy); carve(cx + 1, cy); carve(cx, cy + 1); carve(cx + 1, cy + 1); };
  let x = Math.floor(cols / 2) - 1;
  const start = { x, y: rows - 1 };
  carve(x, rows - 1); carve(x + 1, rows - 1);
  for (let y = rows - 2; y >= 0; y--) {
    carve2(x, y);
    if (y % 7 === 3) {
      for (let dy = -2; dy <= 3; dy++) for (let dx = -2; dx <= 3; dx++) if (x + dx >= 1 && x + dx < cols - 1 && y + dy >= 1) carve(x + dx, y + dy);
    } else if (y > 0 && seedRandom() < 0.6) {
      const dir = x <= 2 ? 1 : x >= cols - 4 ? -1 : seedRandom() < 0.5 ? -1 : 1;
      const run = 1 + rnd(5);
      for (let i = 0; i < run; i++) {
        const nx = x + dir;
        if (nx < 1 || nx >= cols - 2) break;
        x = nx;
        carve2(x, y);
      }
    }
  }
  const exit = { x, y: 0 };
  const mainLen = path.length;
  // side branches and loops for more routes
  for (let b = 0; b < 6 + Math.floor(afterLevel / 10); b++) {
    const from = path[2 + rnd(Math.max(1, mainLen - 4))];
    const dir = seedRandom() < 0.5 ? -1 : 1;
    let bx = from.x, by = from.y;
    const len = 2 + rnd(4);
    for (let i = 0; i < len; i++) {
      const nx = bx + dir;
      if (nx < 1 || nx >= cols - 1) break;
      bx = nx;
      carve(bx, by);
    }
    const vertical = 2 + rnd(5);
    const vdir = seedRandom() < 0.7 ? -1 : 1;
    for (let i = 0; i < vertical; i++) {
      const ny = by + vdir;
      if (ny < 1 || ny >= rows - 1) break;
      by = ny;
      carve(bx, by);
    }
    if (seedRandom() < 0.7) {
      const back = -dir;
      for (let i = 0; i < len + 1; i++) {
        const nx = bx + back;
        if (nx < 1 || nx >= cols - 1) break;
        bx = nx;
        carve(bx, by);
        if (grid[by][bx] === Cell.Floor && path.some((p) => p.x === bx && p.y === by && path.indexOf(p) < mainLen)) break;
      }
    }
  }
  const kinds = grid.map((row) => row.map(() => null)) as (CrystalKind | null)[][];
  const dist = (p: { x: number; y: number }) => Math.abs(p.x - start.x) + Math.abs(p.y - start.y);
  const corridor = path.filter((p) => p.y > 1 && dist(p) >= 3 && !(p.x === exit.x && p.y === exit.y));
  const enemies: { x: number; y: number; kind: EnemyKind }[] = [];
  const balls: { x: number; y: number }[] = [];
  const enemyCount = Math.min(9, 5 + Math.floor(afterLevel / 10));
  const ballCount = Math.min(22, 11 + Math.floor(afterLevel / 5));
  const shuffled = shuffle([...corridor]);
  const taken = new Set<string>();
  const far = (p: { x: number; y: number }, min: number) => [...taken].every((k) => { const [tx, ty] = k.split(",").map(Number); return Math.abs(tx - p.x) + Math.abs(ty - p.y) >= min; });
  for (const p of shuffled) {
    if (balls.length >= ballCount) break;
    if (far(p, 2)) { balls.push(p); taken.add(`${p.x},${p.y}`); }
  }
  // enemies spread along the whole tunnel: one per vertical band
  const bandH = (rows - 8) / enemyCount;
  for (let b = 0; b < enemyCount; b++) {
    const yTop = 1 + b * bandH, yBot = 1 + (b + 1) * bandH;
    const p = shuffled.find((q) => q.y >= yTop && q.y < yBot && dist(q) >= 6 && far(q, 2));
    if (!p) continue;
    enemies.push({ ...p, kind: afterLevel >= 20 && seedRandom() < 0.5 ? "spider" : enemies.length % 3 === 1 ? "scorpion" : "spider" });
    taken.add(`${p.x},${p.y}`);
  }
  balls.forEach((b) => { grid[b.y][b.x] = Cell.Crystal; kinds[b.y][b.x] = "ball"; });
  return { grid, kinds, player: start, enemies, reserve: [], cap: 99, exit };
}
